import Pubnub from 'pubnub';

export interface PubnubConfig {
  publishKey?: string;
  subscribeKey?: string;
  logLevel?: string;
}

export function toPubnubLogLevel(level: string): Pubnub.LogLevel {
  switch (level.toLowerCase()) {
    case 'error':
      return Pubnub.LogLevel.Error;
    case 'warn':
      return Pubnub.LogLevel.Warn;
    case 'debug':
      return Pubnub.LogLevel.Debug;
    case 'info':
      return Pubnub.LogLevel.Info;
    case 'trace':
      return Pubnub.LogLevel.Trace;
    case 'none':
    default:
      return Pubnub.LogLevel.None;
  }
}

export function fromPubnubLogLevel(level: Pubnub.LogLevel): string {
  switch (level) {
    case Pubnub.LogLevel.Error:
      return 'error';
    case Pubnub.LogLevel.Warn:
      return 'warn';
    case Pubnub.LogLevel.Info:
      return 'info';
    case Pubnub.LogLevel.Debug:
      return 'debug';
    case Pubnub.LogLevel.Trace:
      return 'trace';
    case Pubnub.LogLevel.None:
    default:
      return 'none';
  }
}

const ignoreProperties = ['crypto', 'timeout', 'keySet', 'getFileUrl'];
const sanitizeValues = [
  'authKey',
  'publishKey',
  'subscribeKey',
  'token',
  'subscriptionKey',
  'auth',
  'accessTokensMap',
];

export function parsePubnubLog(log: Pubnub.LogMessage): {
  message: string;
  sanitizedLog: string;
} {
  const message = parseLogMessage(log);
  const sanitizedLog = sanitizePubnubLog(log, sanitizeValues, ignoreProperties);
  return { message, sanitizedLog };
}

function parseLogMessage(log: Pubnub.LogMessage): string {
  if (log == null) {
    return 'No log provided';
  } else if (log.message == null) {
    return 'No message provided';
  } else if (typeof log.message === 'string') {
    return log.message;
  } else if (log.messageType === 'object' && log.details != null) {
    return log.details;
  } else {
    // cannot stringify log.message since it creates a circular reference error.
    return 'Pubnub Log';
  }
}

function sanitizePubnubLog(
  log: Pubnub.LogMessage,
  redactValues: string[] = [],
  ignoreProps: string[] = [],
): string {
  return JSON.stringify(log, logMessageSanitizer(redactValues, ignoreProps));
}

function logMessageSanitizer(redactValues: string[] = [], ignoreProps: string[] = []) {
  const seen = new WeakSet();
  return (key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return; // remove circular reference
      }
      seen.add(value);
    }
    if (key.startsWith('_') || ignoreProps.includes(key)) {
      return; // exclude private properties and ignored keys
    }
    if (redactValues.includes(key)) {
      return '****'; // redact sensitive value
    }
    if (typeof value === 'string' && isValidUrl(value)) {
      const url = new URL(value);
      if (url.username || url.password) {
        // redact credentials in URL
        url.username = url.password = '****'; // redact username and password
      }
      url.searchParams.forEach((_, paramKey) => {
        // ignore params
        if (ignoreProps.includes(paramKey)) {
          url.searchParams.delete(paramKey);
        }
        // redact param values
        if (redactValues.includes(paramKey)) {
          url.searchParams.set(paramKey, '****'); // redact sensitive query parameters
        }
      });
      return url.toString();
    }
    return value;
  };
}

function isValidUrl(maybeUrl?: string): boolean {
  try {
    if (maybeUrl == null || maybeUrl.trim() === '') {
      return false;
    }
    new URL(maybeUrl);
    return true;
  } catch (error) {
    return false;
  }
}
